'use client'

import React from 'react'
import { User } from 'lucide-react'
import { UserCircle } from 'solar-icons'
import { toast } from 'sonner'
import { searchCustomer } from '@/lib/actions'
import { useCheckout, useModals, useSelectedCustomer } from '@/lib/hooks'

import {
  Avatar,
  AvatarFallback,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from '@/components/ui'
import { CustomerList, SearchBar } from '@/components/molecules'

const validatePhoneNumber = (input: string): boolean => {
  // Remove all whitespace and hyphens for validation
  const cleanedInput = input.replace(/[\s-]/g, '')

  // Check if input is empty
  if (!cleanedInput) {
    return false
  }

  // Sri Lankan phone number formats:
  // - Local with leading 0: 0xxxxxxxxx (10 digits starting with 0)
  // - Local without leading 0: xxxxxxxxx (9 digits, e.g., 777123456)
  // - Mobile: 07xxxxxxxx or 7xxxxxxxx
  // - International: +94xxxxxxxxx (12 digits with country code)

  // Check for Sri Lankan local format with leading 0 (10 digits starting with 0)
  const localFormatWithZero = /^0[0-9]{9}$/
  if (localFormatWithZero.test(cleanedInput)) {
    return true
  }

  // Check for Sri Lankan format without leading 0 (9 digits)
  const localFormatWithoutZero = /^[1-9][0-9]{8}$/
  if (localFormatWithoutZero.test(cleanedInput)) {
    return true
  }

  // Check for international format (+ followed by country code and number)
  const internationalFormat = /^\+[1-9][0-9]{7,14}$/
  if (internationalFormat.test(cleanedInput)) {
    return true
  }

  return false
}

export const SelectCustomer = () => {
  const { setCurrentStep } = useCheckout()
  const { openModal } = useModals()
  const { setAsGuestCustomer, setCustomer, customer, setContinueAsGuest } =
    useSelectedCustomer()

  const [input, setInput] = React.useState('')

  React.useEffect(() => {
    setInput(customer?.phone ?? '')
  }, [customer])

  const [showSearchDropdown, setShowSearchDropdown] = React.useState(false)
  const [customers, setCustomers] = React.useState<Customer[]>([])

  const handleSearchSubmit = React.useCallback(
    async (_data: FormData) => {
      if (!input) {
        toast.error('Please enter a phone number or customer name')
        return
      }

      // Validate phone number if input appears to be a phone number
      const isPhoneNumber = /^[0-9+\s-]/.test(input)
      if (isPhoneNumber && !validatePhoneNumber(input)) {
        toast.error(
          'Invalid phone number format. Please enter a valid phone number (e.g., 777123456, 0777123456, or +94777123456)',
        )
        return
      }

      // Remove spaces and hyphens before passing to backend
      const cleanedInput = input.replace(/[\s-]/g, '')
      const customers = await searchCustomer(cleanedInput)
      setCustomers(customers)
      setShowSearchDropdown(true)
    },
    [input],
  )

  const handleSelectionChange = React.useCallback(
    async (id: any) => {
      const selectedCustomer = customers.find((c) => c.id === id?.currentKey)

      if (!selectedCustomer) {
        console.error(`No customer found with id: ${id?.currentKey}`)
        await setAsGuestCustomer()
        return
      }

      await setCustomer(selectedCustomer)
      setInput(selectedCustomer?.phone ?? '')
      setCurrentStep('SELECT_PAYMENT_METHOD')
      setShowSearchDropdown(false)
    },
    [customers, setCustomer, setCurrentStep, setAsGuestCustomer],
  )

  const handleInputChange = React.useCallback((value: string) => {
    setInput(value)
  }, [])

  return (
    <div className="flex h-full w-full flex-col items-center gap-10">
      <div className="flex w-full flex-grow flex-col items-center gap-7">
        <Avatar className="relative size-[100px] overflow-visible bg-white p-1 shadow-md">
          <AvatarFallback className="border border-gray-100 bg-gray-50 text-2xl text-gray-400 dark:border-gray-700">
            <User className="size-10" strokeWidth={1.5} />
          </AvatarFallback>
        </Avatar>

        <div className="flex w-full flex-col">
          <form
            className="flex w-full flex-col items-center gap-6"
            action={handleSearchSubmit}
          >
            <h2 className="text-lg font-medium">Search Customer</h2>
            <Popover open={showSearchDropdown}>
              <SearchBar
                autoFocus
                placeholder="Enter phone number or name"
                className="w-full bg-white pl-2"
                value={input ?? customer?.phone}
                onChangeValue={handleInputChange}
                type="text"
                isCustomerSearch={true}
              />
              <PopoverTrigger className="w-full" />
              <PopoverContent
                className="w-[280px] rounded-lg shadow-block md:w-[400px] lg:w-[500px]"
                sideOffset={-16}
              >
                <ScrollArea className="flex flex-col">
                  <CustomerList
                    customers={customers}
                    onSelectionChange={handleSelectionChange}
                  />
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </form>

          {customer && (
            <Button
              variant="brand"
              className="w-full gap-2"
              size="lg"
              onClick={() => {
                setCurrentStep('SELECT_PAYMENT_METHOD')
              }}
            >
              Continue as {customer?.name}
            </Button>
          )}
        </div>
      </div>

      <div className="flex w-full flex-col gap-2 lg:flex-row lg:items-center">
        <Button
          variant="outline"
          className="w-full gap-2"
          size="lg"
          onClick={() => {
            setContinueAsGuest(false)
            openModal('CREATE_CUSTOMER')
          }}
        >
          <UserCircle strokeWidth={1.2} className="size-5" />
          Create New Customer
        </Button>
        <Button
          variant="default"
          className="w-full gap-2"
          size="lg"
          onClick={() => {
            setContinueAsGuest(true)
            setCurrentStep('SELECT_PAYMENT_METHOD')
          }}
        >
          <UserCircle strokeWidth={1.2} className="size-5" />
          Continue as Guest
        </Button>
      </div>
    </div>
  )
}
